<template>
  <div v-if="reportType == reportTypes.Tabla" class="col-12 mb-3 py-0">
    <SQLInput
      ref="sqlInputRef"
      :defaultCode="defaultCode"
      :dataSourceId="dataSourceId"
      :filters="reportFilters"
      label="Código SQL"
    />
  </div>

  <Form ref="formRef" v-bind="formConfigRef">
    <template #newPbix__after>
      <Button
        v-if="idInforme == null && !uploadedPbix"
        label="Nuevo PBIX"
        icon="pi pi-upload"
        class="w-full"
        :disabled="selectedGuidWorkspace === null"
        @click="emit('show-upload-pbix-modal')"
      />
    </template>

    <template #after-all>
      <div v-show="showLandingPageEditor" class="col-12 py-0">
        <div class="editor-label mb-1">Pie de página</div>
        <div :id="quillEditorId"></div>
        <div class="sqleditor-custom-input"></div>
        <div class="flex align-items-center gap-2 text-sm mt-2">
          <i class="pi pi-info-circle"></i>
          <span
            >Para insertar la URL del informe en medio del pie de página,
            utilice la sintaxis <i>[url]</i></span
          >
        </div>
      </div>
    </template>
  </Form>

  <div
    v-if="idInforme == null && uploadedPbix"
    class="text-sm mt-3 flex align-items-center gap-2"
  >
    <i class="pi pi-check-circle text-success"></i>
    Archivo PBIX subido correctamente. Si necesita realizar cambios, puede
    cambiar el PBIX al actualizar el informe.
  </div>

  <!-- Prefilters -->
  <div v-if="prefiltrar" class="col-12">
    <!-- Add filter button -->
    <div class="flex flex-column align-items-start gap-2 mb-3">
      <Button
        label="Añadir filtro"
        icon="add-filter-button pi pi-plus"
        @click="filters++"
      />
      <small v-if="filtersError" class="p-error">{{ filtersError }}</small>
    </div>

    <!-- Filters form -->
    <div v-for="n in filters" :key="n" class="filter-form grid mb-2">
      <!-- Tabla -->
      <div class="col-12 md:col-6 flex flex-column gap-1">
        <label class="custom-label">Tabla</label>
        <InputText
          v-model="filterTables[n - 1]"
          placeholder="Tabla"
          class="w-full table-input"
        />
      </div>

      <!-- Columna -->
      <div class="col-12 md:col-6 flex flex-column gap-1">
        <label class="custom-label">Columna</label>
        <InputText
          v-model="filterColumns[n - 1]"
          placeholder="Columna"
          class="w-full column-input"
        />
      </div>

      <!-- Operador -->
      <div class="col-12 md:col-4 flex flex-column gap-1">
        <label class="custom-label">Operador</label>
        <Dropdown
          v-model="filterOperators[n - 1]"
          placeholder="Operador"
          :options="operators"
          optionValue="operator"
          optionLabel="label"
          class="w-full operator-input"
        />
      </div>

      <!-- Valor -->
      <div class="col-12 md:col-6 flex flex-column gap-1">
        <label class="custom-label">Valor</label>
        <InputText
          v-if="filterOperators[n - 1] != 'in'"
          v-model="filterValues[n - 1]"
          placeholder="Valor"
          class="w-full value-input"
        />
        <template v-else>
          <Chips
            v-model="filterValues[n - 1]"
            placeholder="Valor"
            class="w-full"
            :addOnBlur="true"
          />
          <div class="flex gap-2 align-items-center text-sm mt-1">
            <i class="pi pi-info-circle"></i>
            <span>Para añadir múltiples valores pulse la tecla "Enter ↵"</span>
          </div>
        </template>
      </div>

      <!-- Borrar filtro -->
      <div class="col-12 md:col-2 flex flex-column gap-1">
        <label class="custom-label opacity-0">&nbsp;</label>
        <Button
          icon="pi pi-trash"
          class="w-full remove-filter-button"
          @click="removeFilter(n - 1)"
          severity="danger"
        />
      </div>

      <div v-if="n < filters" class="col-12 mb-2">
        <hr class="horizontal-row-secondary" />
      </div>
    </div>

    <!-- URL filter info -->
    <Panel header="Prefiltrar mediante URL" toggleable :collapsed="true">
      <div class="flex flex-column gap-2">
        <div>
          Si se quiere filtrar el informe mediante la URL, defina la siguiente
          estructura:
        </div>
        <i class="font-bold">&filter=$tabla/$columna $operador $valor</i>
        <div class="mt-3">
          Por ejemplo, si queremos filtrar para obtener unos resultados según un
          único valor:
        </div>
        <i class="font-bold">&filter=Empresas/cif eq '123456A'</i>
        <div class="mt-3">
          O por el contrario, si queremos filtrar según un conjunto de opciones:
        </div>
        <i class="font-bold"
          >&filter=Usuarios/nombre in ('PEPE', 'FRANCISCO')</i
        >
      </div>
    </Panel>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, inject } from "vue";

import Quill from "quill";
import InputText from "primevue/inputtext";
import Dropdown from "primevue/dropdown";
import Button from "primevue/button";
import Chips from "primevue/chips";
import Panel from "primevue/panel";

import "quill/dist/quill.snow.css";

import { Form, useImmer } from "@sttedra_ui/framework";
import { watchDebounced } from "@vueuse/core";

import SQLInput from "@/components/partials/SQLInput.vue";

import useFormConfig, {
  getWorkspaces,
  getDataSources,
  getMapTypes,
  getSubMapTypes,
} from "@/pages/_root/reports-dialog/form-two/report-form-two.view-config.js";
import {
  removeCustomErrors,
  addCustomErrors,
} from "@/composables/_commons/helper.js";
import { reportTypes } from "@/composables/_commons/enums.js";

// #region Props, Emits
const emit = defineEmits("report-selected", "show-upload-pbix-modal");
const reportFilters = inject('reportFilters');

defineProps({
  idInforme: {
    type: String,
    default: null,
  },
});
// #endregion

// #region Params, Ref, Reactives, Computed
const operators = [
  {
    operator: "and",
    label: "Y",
  },
  {
    operator: "eq",
    label: "Igual",
  },
  {
    operator: "ne",
    label: "Distinto",
  },
  {
    operator: "ge",
    label: "Mayor que o igual",
  },
  {
    operator: "gt",
    label: "Mayor que",
  },
  {
    operator: "le",
    label: "Menor que o igual",
  },
  {
    operator: "lt",
    label: "Menor que",
  },
  {
    operator: "in",
    label: "Incluye",
  },
];
const filters = ref(0);
const filtersError = ref("");
const filterIds = ref([]);
const filterTables = ref([]);
const filterColumns = ref([]);
const filterOperators = ref([]);
const filterValues = ref([]);
const formRef = ref(null);
const formConfig = ref(null);
const { ref: formConfigRef, update: updateFormConfig } = useImmer(formConfig);
const register = ref(null);
const defaultCode = ref("");
const reportType = ref("");
const sqlInputRef = ref(null);
const quillEditorId = "quill-editor";
const quillEditor = ref(null);
const loadingData = ref(false);
const selectedGuidWorkspace = ref(null);
const selectedUidInforme = ref(null);
const uploadedPbix = ref(false);

const dataSourceId = computed(() => {
  const value = formRef.value?.state$$.values?.fuenteDatosDto;
  return value;
});

const prefiltrar = computed(() => {
  return formRef.value?.state$$.values?.prefiltrar;
});

const showFooter = computed(() => {
  // if the form tieneLandingPage is active, show the footer section
  return formRef.value?.state$$.values?.tieneLandingPage;
});

const showLandingPageEditor = computed(() => {
  return (
    reportType.value == reportTypes.Grafico ||
    (reportType.value == reportTypes.URL && showFooter.value)
  );
});
// #endregion

// #region Functions
async function loadForm(data, type) {
  loadingData.value = true;
  reportType.value = type;

  await getWorkspaces();
  await getDataSources();
  await getMapTypes();
  await getSubMapTypes();

  if (Object.keys(data).length == 0) {
    formConfig.value = new useFormConfig(null, type);
  } else {
    register.value = data;

    const object = Object.keys(data).map((key) => {
      return {
        name: key,
        value: data[key],
      };
    });

    formConfig.value = new useFormConfig(object, type);

    nextTick(() => {
      if (formRef.value) {
        if (data.fuenteDatosDto) {
          formRef.value.state$$.values.fuenteDatosDto =
            data.fuenteDatosDto.idFuenteDatos;
        }

        if (data.query?.queryText) {
          defaultCode.value = data.query.queryText;
        }

        if (data.piePagina) {
          const quillEditor = getQuillEditorElement();
          quillEditor.innerHTML = data.piePagina;
        }

        if (data.prefiltros?.length > 0) {
          formRef.value.state$$.values.prefiltrar = true;
          filters.value = data.prefiltros.length;

          data.prefiltros.forEach((prefiltro) => {
            const [tabla, columna] = prefiltro.nombreTablaColumna.split("/");

            filterIds.value.push(prefiltro.idPreFiltro);
            filterTables.value.push(tabla);
            filterColumns.value.push(columna);
            filterOperators.value.push(prefiltro.operador);

            if (prefiltro.operador == "in") {
              prefiltro.valor = prefiltro.valor.split(",");
            }

            filterValues.value.push(prefiltro.valor);
          });
        }
      }
    });
  }

  nextTick(() => {
    const quillEditorElement = document.querySelector(`#${quillEditorId}`);

    if (quillEditorElement) {
      quillEditor.value = new Quill(`#${quillEditorId}`, {
        theme: "snow",
      });
    }
  });

  setTimeout(() => {
    loadingData.value = false;
  }, 1);
}

function validatePrefiltrar() {
  let filtersInvalid = false;

  if (prefiltrar.value) {
    if (filters.value == 0) {
      filtersError.value = "Debe añadir al menos un filtro";
      filtersInvalid = true;
    }

    if (filters.value > 0) {
      for (let index = 0; index < filters.value; index++) {
        const filterName = filterTables.value[index];
        const filterColumn = filterColumns.value[index];
        const filterOperator = filterOperators.value[index];
        const filterValue = filterValues.value[index];

        if (!filterName || !filterColumn || !filterOperator || !filterValue) {
          filtersInvalid = true;
          filtersError.value =
            "Todos los campos de los filtros son obligatorios";
        }
      }
    }

    if (!filtersInvalid) {
      filtersError.value = "";
    }
  }

  return filtersInvalid;
}

async function validateForm() {
  let invalid = !(await formRef.value?.v$Ref.$validate());

  const sqlEditorDiv = document.querySelector(".sqleditor-custom-input");
  removeCustomErrors(".sqleditor-custom-input");

  const quillContentElement = getQuillContentElement();

  if (
    showFooter.value &&
    (!quillContentElement.innerHTML || quillContentElement.innerHTML == "")
  ) {
    invalid = true;
    addCustomErrors(sqlEditorDiv, "El pie de página es obligatorio");
  }

  if (reportType.value == reportTypes.Tabla) {
    const sqlCode = sqlInputRef.value?.code;

    if (!sqlCode) {
      invalid = true;
      sqlInputRef.value.error = "El código SQL no puede estar vacío";
    } else {
      sqlInputRef.value.error = "";
    }
  }

  const filtersInvalid = validatePrefiltrar();

  invalid = invalid || filtersInvalid;

  return invalid;
}

function getData(registerId) {
  const data = JSON.parse(JSON.stringify(formRef.value?.state$$.values));

  data.query = sqlInputRef.value?.code ? sqlInputRef.value.code : null;
  data.fuenteDatos = dataSourceId.value;
  data.tieneLandingPage =
    data.tieneLandingPage == null ? false : data.tieneLandingPage;

  let uidInforme = data.uidInforme;

  if (uidInforme && typeof uidInforme == "object") {
    data.GuidReportPowerBI = uidInforme.id;
  } else {
    data.GuidReportPowerBI = uidInforme;
  }

  delete data.uidInforme;

  const quillContentElement = getQuillContentElement();

  if (quillContentElement) {
    data.piePagina = quillContentElement.innerHTML;
  }

  if (!showLandingPageEditor.value || data.piePagina == "<p><br></p>") {
    data.piePagina = null;
  }

  if (prefiltrar.value) {
    data.prefiltros = [];

    for (let index = 0; index < filters.value; index++) {
      const prefilter = {
        nombreTablaColumna: `${filterTables.value[index]}/${filterColumns.value[index]}`,
        operador: filterOperators.value[index],
        valor: filterValues.value[index].toString(),
      };

      if (registerId && filterIds.value[index]) {
        prefilter.idPreFiltro = filterIds.value[index];
      }

      data.prefiltros.push(prefilter);
    }
  }

  return data;
}

function getQuillEditorElement() {
  return document.getElementById(quillEditorId);
}

function getQuillContentElement() {
  const quillEditor = getQuillEditorElement();
  return quillEditor.querySelector(".ql-editor");
}

function removeFilter(n) {
  loadingData.value = true;

  filterTables.value.splice(n, 1);
  filterColumns.value.splice(n, 1);
  filterOperators.value.splice(n, 1);
  filterValues.value.splice(n, 1);

  filters.value--;

  nextTick(() => {
    loadingData.value = false;
  });
}

function handleReportHasRoles() {
  if (selectedUidInforme.value && selectedGuidWorkspace.value) {
    emit(
      "report-selected",
      selectedUidInforme.value,
      selectedGuidWorkspace.value,
    );
  }
}

function loadPbixUid(uidInforme) {
  uploadedPbix.value = true;
  formRef.value.state$$.values.uidInforme = uidInforme;
}
// #endregion

// #region Effects, Hooks
watch(
  () => formRef?.value?.state$$?.values?.tipoMapa,
  (value) => {
    updateFormConfig((config) => {
      const items = config.items;

      items.forEach((item) => {
        if (item.field == "tipoSubmapa") {
          if (value) {
            item.properties = {
              disabled: value == null,
            };
          } else {
            item.properties = {
              disabled: true,
            };

            formRef.value.state$$.values.tipoSubmapa = null;
          }
        }
      });

      return config;
    });
  },
);

watch(
  () => formRef?.value?.state$$?.values?.guidWorkspace,
  (value) => {
    selectedGuidWorkspace.value = value;
    handleReportHasRoles();

    updateFormConfig((config) => {
      const items = config.items;

      items.forEach((item) => {
        if (item.field == "uidInforme") {
          item.properties = {
            disabled: value == null,
          };
        }
      });

      return config;
    });
  },
);

watchDebounced(
  () => formRef?.value?.state$$?.values?.uidInforme,
  (value) => {
    if (value && typeof value == "object") {
      selectedUidInforme.value = value.id;
      handleReportHasRoles();
    } else {
      selectedUidInforme.value = null;
    }
  },
  { debounce: 500 },
);

watch(
  () => [...filterOperators.value],
  (newVal, oldVal) => {
    if (!loadingData.value) {
      let changedIndex = null;

      for (let index = 0; index < filterOperators.value.length; index++) {
        const newElement = newVal[index];
        const oldElement = oldVal[index];

        if (newElement != oldElement) {
          changedIndex = index;
        }
      }

      if (changedIndex > -1) {
        filterValues.value[changedIndex] = null;
      }
    }
  },
);
// #endregion

// #region Provide, Expose
defineExpose({
  formRef,
  loadForm,
  validateForm,
  getData,
  loadPbixUid,
});
// #endregion
</script>

<style lang="scss" scoped>
.editor-label {
  color: #6b7280;
}
</style>
